//////////////////////////////////////////////////////////////////////////////
//
//  광고 클릭 로그 목록 화면
//
//  작성자: MediInStep Team
//  생성일: 2025-12-22
//
//  설명: 전면 광고 클릭 로그 목록 조회, 페이징 기능
//
//////////////////////////////////////////////////////////////////////////////

import { Component } from "react";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import DashboardLayout from "ScreenLayouts/ContainerLayout/DashboardLayout";
import DashboardNavbar from "ScreenLayouts/NavbarLayout/DashboardNavbar";
import Footer from "ScreenFooters/DefaultFooter";
import BasicTable from "ScreenLayouts/TableLayout/BasicTableLayout/BasicTable";

// Network
import { request } from "app/network";
import { useMaterialUIController } from "context";
import { formatLocalDateTime, formatLocalDate, formatLocalTime } from "utils/dateFormatter";

interface AdClickLogListState {
    logs: any[];
    pageSize: number;
    pageNumber: number;
    totalCount: number;
    loading: boolean;
    searchTerm: string;
}

const PAGE_SIZE_STORAGE_KEY = "mediinstep_ad_click_log_list_page_size";

class AdClickLogListScreenClass extends Component<any, AdClickLogListState> {
    constructor(props: any) {
        super(props);
        const savedPageSize = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
        const initialPageSize = savedPageSize ? parseInt(savedPageSize, 10) : 10;

        this.state = {
            logs: [],
            pageSize: initialPageSize,
            pageNumber: 1,
            totalCount: 0,
            loading: false,
            searchTerm: "",
        };
    }

    componentDidMount() {
        this.loadLogs();
    }

    loadLogs = async () => {
        try {
            this.setState({ loading: true });

            const param = {
                PageNumber: this.state.pageNumber,
                PageSize: this.state.pageSize,
                SearchKeyword: this.state.searchTerm || undefined,
            };

            const res = await request("InterstitialAd.clickLogsPagedList", { paramz: param });

            if (res && res.data) {
                const pagedList = res.data;
                const logs = (pagedList.items || []).map((log: any) => {
                    const rawClickDt = log.clickDt || null;
                    const hasTimezoneClickDt = rawClickDt && /(Z|[+-]\d{2}:?\d{2})$/.test(rawClickDt);
                    const clickDt = rawClickDt ? (hasTimezoneClickDt ? rawClickDt : `${rawClickDt}Z`) : null;

                    return {
                        id: log.seqId,
                        clickDt,
                        adTitle: log.adTitle || "-",
                        userName: log.userName || "비회원",
                        clickAtSeconds: log.clickAtSeconds !== null ? `${log.clickAtSeconds}초` : "-",
                        linkType: log.linkType || "-",
                        linkTarget: log.linkTarget || "-",
                        screenName: log.screenName || "-",
                        deviceType: log.deviceType || "-",
                    };
                });

                this.setState({
                    logs,
                    totalCount: pagedList.totalCount || 0,
                    loading: false,
                });
            } else {
                this.setState({ loading: false });
            }
        } catch (error: any) {
            console.error("광고 클릭 로그 목록 조회 실패:", error);
            this.setState({ loading: false });
        }
    };

    handlePageChange = (pageIndex: number) => {
        this.setState({ pageNumber: pageIndex + 1 }, () => {
            this.loadLogs();
        });
    };

    handlePageSizeChange = (pageSize: number) => {
        localStorage.setItem(PAGE_SIZE_STORAGE_KEY, pageSize.toString());
        this.setState({ pageSize, pageNumber: 1 }, () => {
            this.loadLogs();
        });
    };

    handleSearch = (searchValue: string) => {
        this.setState({ searchTerm: searchValue, pageNumber: 1 }, () => {
            this.loadLogs();
        });
    };

    render() {
        const { logs, pageSize, loading, totalCount, pageNumber } = this.state;

        const renderDateCell = (value: string | null) => {
            if (!value || value === "-") return "-";

            return (
                <MDBox lineHeight={1.2} textAlign="center">
                    <MDTypography variant="caption" display="block" color="text" fontWeight="medium">
                        {formatLocalDate(value)}
                    </MDTypography>
                    <MDTypography variant="caption" display="block" color="text">
                        {formatLocalTime(value)}
                    </MDTypography>
                </MDBox>
            );
        };

        const columns = [
            {
                Header: "클릭일시",
                accessor: "clickDt",
                align: "center",
                width: "14%",
                Cell: ({ value }: any) => renderDateCell(value)
            },
            { Header: "광고명", accessor: "adTitle", align: "left", width: "15%" },
            { Header: "사용자", accessor: "userName", align: "left", width: "10%" },
            { Header: "클릭지점", accessor: "clickAtSeconds", align: "center", width: "8%" },
            { Header: "링크타입", accessor: "linkType", align: "center", width: "8%" },
            { Header: "링크대상", accessor: "linkTarget", align: "left", width: "20%" },
            { Header: "디바이스", accessor: "deviceType", align: "center", width: "10%" },
        ];

        const rows = logs;

        const totalPages = Math.ceil(totalCount / pageSize);
        const pageCountArray = Array.from({ length: totalPages }, (_, i) => i);
        const currentPageIndex = pageNumber - 1;
        const pageTenNumber = Math.floor(currentPageIndex / 10) * 10;

        const paginationCriteria = {
            PageNumber: currentPageIndex,
            PageCount: pageCountArray,
            PageTenNumber: pageTenNumber,
            totalCount: totalCount,
        };

        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox pt={6} pb={3} position="relative">
                    <Card sx={{ "& table": { tableLayout: "fixed", width: "100%" } }}>
                        <MDBox p={3} lineHeight={1}>
                            <MDBox display="flex" justifyContent="space-between" alignItems="center">
                                <MDBox display={"flex"} alignContent="center">
                                    <Icon fontSize={"medium"}>touch_app</Icon>
                                    <MDTypography ml={1} variant="h5" fontWeight="medium">
                                        광고 클릭 기록 ( AD CLICK LOGS )
                                    </MDTypography>
                                </MDBox>
                            </MDBox>
                            <MDTypography variant="button" color="text" mt={1}>
                                광고 클릭 로그 목록 (전체: {totalCount}개)
                            </MDTypography>
                        </MDBox>
                        <BasicTable
                            canSearch={true}
                            onSearch={this.handleSearch}
                            criteria={paginationCriteria}
                            storePageSize={pageSize}
                            goPage={this.handlePageChange}
                            setStorePageSize={this.handlePageSizeChange}
                            table={{ columns, rows }}
                            isSorted={false}
                            entriesPerPage={{
                                defaultValue: pageSize,
                                entries: [5, 10, 15, 20, 25, 30, 40, 50],
                            }}
                            showTotalEntries={true}
                            noEndBorder
                        />
                        {loading && (
                            <MDBox
                                position="absolute"
                                top={0}
                                left={0}
                                right={0}
                                bottom={0}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                bgcolor="rgba(0, 0, 0, 0.3)"
                                sx={{ pointerEvents: "none" }}
                            >
                                <MDBox display="flex" gap={1.5}>
                                    <CircularProgress size={40} sx={{ color: "#49a3f1" }} />
                                </MDBox>
                            </MDBox>
                        )}
                    </Card>
                </MDBox>
                <Footer />
            </DashboardLayout>
        );
    }
}

function AdClickLogListScreen(props: any) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    return <AdClickLogListScreenClass {...props} darkMode={darkMode} />;
}

export default AdClickLogListScreen;
